import { useMemo, useRef, useCallback, useEffect, useState } from 'react'
import type { ResponseOption } from '../../../../../redux/flows/portal/currentPatient'
import {
  setPatientNotes,
  postPatientNotesAsync,
  priorityAnswersAsync,
  updateResponseOptionPriority,
  clearResponseOptionPriority,
} from '../../../../../redux/flows/portal/currentPatient'
import { useAppSelector, useAppDispatch } from '../../../../../redux/store'

const useInfo = () => {
  const dispatch = useAppDispatch()
  const currentPatient = useAppSelector((state) => state.currentPatientPortal.currentPatient)
  const priorityAnswersStatus = useAppSelector((state) => state.currentPatientPortal.priorityAnswersStatus)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)
  const [notesValue, setNotesValue] = useState('')

  const info = useMemo(() => {
    const responses = currentPatient?.questionResponses || []

    const sorted = [...responses].sort((a, b) => a.question.order - b.question.order)

    return sorted.map((item) => {
      // Clean title
      const title = item.question.text.replace(/conditions?[^\w]?/i, '').trim()

      const options = [...item.responseOptions]

      // ---- PRIORITY SORTING LOGIC ----
      const priorityOptions = options
        .filter((ro) => (ro.priority || 0) > 0)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .slice(0, 4) // top 4 only

      const nonPriorityOptions = options
        .filter((ro) => (ro.priority || 0) === 0)
        .sort((a, b) => a.option.order - b.option.order)

      const responseOptions = [...priorityOptions, ...nonPriorityOptions]
      // ----------------------------------

      return { title, responseOptions }
    })
  }, [currentPatient])

  // Filter out groups where any responseOption has text "None" (user selected "None")
  const filteredInfo = info?.filter((group) => {
    const hasNoneOption = group.responseOptions.some((ro) => ro.option.text === 'None')
    return !hasNoneOption
  })

  // Get all response options with their priorities
  const getAllResponseOptions = useCallback((): ResponseOption[] => {
    if (!currentPatient?.questionResponses) return []
    return currentPatient.questionResponses.flatMap((qr) => qr.responseOptions)
  }, [currentPatient])

  // Check if max priorities (4) have been reached
  const hasMaxPriorities = useMemo(() => {
    const allOptions = getAllResponseOptions()
    const selectedCount = allOptions.filter((ro) => (ro.priority || 0) > 0).length
    return selectedCount >= 4
  }, [getAllResponseOptions])

  // Handle priority selection/deselection
  const handlePriorityToggle = useCallback(
    (responseOptionId: string) => {
      const allOptions = getAllResponseOptions()
      const selectedOption = allOptions.find((ro) => ro.id === responseOptionId)
      const currentPriority = selectedOption?.priority || 0

      // Get all currently selected options (priority > 0), sorted by priority (1, 2, 3, 4)
      const selectedOptions = allOptions
        .filter((ro) => (ro.priority || 0) > 0)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))

      if (currentPriority > 0) {
        // Deselect: set priority to 0 and shift others up
        // Update Redux optimistically first
        dispatch(clearResponseOptionPriority({ responseOptionId }))

        // Update priorities for options that were after this one (optimistically)
        selectedOptions
          .filter((ro) => (ro.priority || 0) > currentPriority)
          .forEach((ro) => {
            const newPriority = (ro.priority || 0) - 1
            dispatch(updateResponseOptionPriority({ responseOptionId: ro.id, priority: newPriority }))
          })

        // Only make API call for the directly deselected option
        dispatch(priorityAnswersAsync({ responseOptionId, priority: 0 }))
      } else {
        // Select: add to the right (next available priority)
        if (selectedOptions.length >= 4) {
          // Remove the 4th priority option (rightmost) - update optimistically
          const toRemove = selectedOptions[selectedOptions.length - 1]
          if (toRemove) {
            dispatch(clearResponseOptionPriority({ responseOptionId: toRemove.id }))
            // Make API call to remove the old one
            dispatch(priorityAnswersAsync({ responseOptionId: toRemove.id, priority: 0 }))
          }
        }

        // New selection gets the next priority (don't shift existing ones)
        const newPriority = selectedOptions.length + 1

        // Set new option to the next priority - update optimistically
        dispatch(updateResponseOptionPriority({ responseOptionId, priority: newPriority }))

        // Make API call for the newly selected option
        dispatch(priorityAnswersAsync({ responseOptionId, priority: newPriority }))
      }
    },
    [dispatch, getAllResponseOptions],
  )

  // Get notes value - show existing notes if available, otherwise empty string

  // Handle notes change with debounced API call
  const handleNotesChange = useCallback(
    (value: string) => {
      // Update Redux state immediately for real-time display
      dispatch(setPatientNotes(value))
      setNotesValue(value)
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }

      // Debounce API call
      debounceTimer.current = setTimeout(() => {
        const patientId = currentPatient?.patient?.id
        if (patientId) {
          dispatch(postPatientNotesAsync({ patientId, notes: value }))
        }
      }, 1000) // 1 second debounce
    },
    [dispatch, currentPatient?.patient?.id],
  )

  useEffect(() => {
    if (currentPatient?.patient?.notes) {
      setNotesValue(currentPatient.patient.notes)
    }
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  return {
    info,
    filteredInfo,
    currentPatient,
    notesValue,
    handleNotesChange,
    handlePriorityToggle,
    priorityAnswersStatus,
    hasMaxPriorities,
  }
}

export default useInfo
